import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { CONTEXT_HEADINGS, firstSentence, type DocumentKind, type TurnRating, type ConversationRole, type ContextHeading } from '@jabberr/core';
import { CONTEXT_TOO_LONG, type ViewTurn } from '../lib/turns.js';
import { isSpeaking, speakingSupported, speakSmart, stopSpeaking } from '../lib/speech.js';
import { Markdown } from './Markdown.js';
import { PinForm } from './PinForm.js';
import { Thinking } from './Thinking.js';
import { roleLabel } from '../lib/plain.js';

export interface PinRequest {
  heading: ContextHeading;
  text: string;
}

export interface TurnViewProps {
  turn: ViewTurn;
  onRetry?: (turn: ViewTurn) => void;
  /** Resolves when the pin is published; the form closes on success. */
  onPin?: (turn: ViewTurn, pin: PinRequest) => Promise<void>;
  canPin?: boolean;
  /** Keep this reply on the project's Outputs page (or drop it again). */
  onSave?: (turn: ViewTurn, saved: boolean) => Promise<void>;
  /** Thumbs on a completed reply; null clears the rating. */
  onRate?: (turn: ViewTurn, rating: TurnRating | null) => Promise<void>;
  /** Link to the reply on the Outputs page, for a document or a kept reply. */
  outputHref?: (turn: ViewTurn) => string;
  /** Flash the turn (a search hit jumped here). */
  highlighted?: boolean;
  /** Keen mode: ask Jabberr to answer a turn the decider stayed silent on. */
  onReplyAnyway?: (turn: ViewTurn) => void;
  /** Role of the conversation, for the role chip on the first assistant turn. */
  role?: ConversationRole;
  /** Index among all assistant turns (for determining "first assistant turn of a run"). */
  assistantIndex?: number;
  /** Index among all turns (for vertical rhythm calculation). */
  turnIndex?: number;
}

/** The current selection when it lies inside this turn's element, else the turn's first sentence. */
export function pinTextFor(turn: ViewTurn, container: HTMLElement | null): string {
  const selection = typeof window !== 'undefined' ? window.getSelection() : null;
  const selected = selection?.toString().trim() ?? '';
  const inside = selected.length > 0 && container !== null && selection !== null && selection.rangeCount > 0 && container.contains(selection.getRangeAt(0).commonAncestorContainer);
  return inside ? selected : firstSentence(turn.text);
}

export function TurnView({ turn, onRetry, onPin, canPin, onSave, onRate, outputHref, highlighted, onReplyAnyway, role, assistantIndex = -1, turnIndex = -1 }: TurnViewProps) {
  const [copied, setCopied] = useState(false);
  const [pinning, setPinning] = useState<{ text: string } | null>(null);
  const [pinBusy, setPinBusy] = useState(false);
  const [speakingThis, setSpeakingThis] = useState(false);
  const canSpeak = speakingSupported();
  useEffect(() => {
    // Another reply's Listen button was pressed, or speech ended elsewhere.
    if (!isSpeaking()) setSpeakingThis(false);
  }, [turn.text]);
  const toggleSpeak = (): void => {
    if (speakingThis) {
      stopSpeaking();
      setSpeakingThis(false);
      return;
    }
    stopSpeaking();
    void speakSmart(turn.text).then((ok) => {
      if (ok) setSpeakingThis(true);
    }, () => {});
  };
  const failed = turn.status === 'failed' || turn.status === 'unavailable';

  if (turn.kind === 'system') {
    return <div className="turn-system small muted" data-turn-key={turn.key}>
      {turn.text} <time dateTime={turn.createdAt}>{new Date(turn.createdAt).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</time>
    </div>;
  }
  if (turn.kind === 'user') {
    return (
      <div className={`turn turn-user status-${turn.status} ${highlighted ? 'turn-flash' : ''}`} data-turn-key={turn.key}>
        {turn.actorName ? <div className="turn-actor small muted">{turn.actorName}</div> : null}
        <div className="bubble">
          <div className="bubble-text">{turn.text}</div>
        </div>
        {turn.status === 'pending' ? <div className="turn-meta muted small">Sending</div> : null}
        {onReplyAnyway ? (
          <div className="turn-meta">
            <span className="muted small" title={turn.interjectP !== null ? `The decision sidecar returned ${Math.round(turn.interjectP * 100)}%.` : turn.keenOutcome === 'skipped_rule' ? undefined : 'The decision did not return a probability.'}>
              {turn.interjectP !== null || turn.keenOutcome === 'skipped_rule' ? 'Jabberr stayed quiet.' : 'Jabberr could not finish deciding.'}
            </span>{' '}
            <button type="button" className="btn btn-small" onClick={() => onReplyAnyway(turn)}>
              Reply anyway
            </button>
          </div>
        ) : null}
        {failed ? (
          <div className={`turn-notice ${failed ? 'status-failed' : ''}`}>
            <svg className="notice-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span className="muted small">
              {turn.status === 'unavailable' ? 'The AI was unavailable.' : `Failed: ${turn.error ?? 'unknown error'}.`}
              {turn.persisted ? ' Your message is saved.' : ' It was not sent.'}
            </span>{' '}
            {onRetry ? (
              <button type="button" className="btn btn-soft" onClick={() => onRetry(turn)}>
                Retry
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  if (turn.kind === 'summary') return null; // shown once, collapsed, at the top of the transcript

  const streaming = turn.status === 'streaming';
  const empty = turn.text.length === 0;
  const full = turn.status === 'failed' && turn.cancelReason === CONTEXT_TOO_LONG;
  const isFirstAssistant = turn.kind === 'assistant' && assistantIndex === 0;
  const answeredRole = turn.answeredRole ?? role;
  const roleName = answeredRole ? roleLabel(answeredRole) : null;
  const roleChip = (isFirstAssistant || turn.answeredRole) && roleName ? { label: roleName } : null;

  return (
    <div className={`turn turn-assistant status-${turn.status} ${highlighted ? 'turn-flash' : ''}`} data-turn-key={turn.key}>
      <div className="turn-actor-row">
        <img className="turn-avatar" src="/apple-touch-icon.png" alt="" aria-hidden="true" />
        <div className="turn-content">
          <div className="turn-author-line">
            <span className="turn-name">Jabberr</span>
            {roleChip ? (
              <span className="chip role-chip">{roleChip.label}</span>
            ) : null}
          </div>
          <Thinking text={turn.reasoning} active={streaming && empty} />
          {turn.tools.length > 0 ? (
            <div className="tool-activity">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              <span className="tool-activity-text">
                {turn.tools.map((tool) => {
                  const urlLabel = tool.name === 'read_page' ? tool.label.slice(tool.label.indexOf('//') + 2) : tool.label;
                  const label = urlLabel.slice(0, 80);
                  return <span key={tool.callId} className="chip tool-chip">{label}</span>;
                })}
              </span>
            </div>
          ) : null}
          {empty && streaming ? (
            <span className="muted small">
              <span className="streaming-caret">|</span>Waiting for a reply...
            </span>
          ) : full ? (
            <div className={`turn-notice status-failed`}>
              <svg className="notice-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span className="muted small">This conversation is full: the project context and the latest exchange no longer fit the model's window.</span>
            </div>
          ) : empty ? (
            <div className={`turn-notice ${turn.status === 'cancelled' ? 'status-cancelled' : turn.status === 'unavailable' ? 'status-unavailable' : ''}`}>
              <svg className="notice-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span className="muted small">
                {turn.status === 'cancelled' ? 'Stopped before any reply.' : turn.status === 'unavailable' ? 'AI unavailable.' : 'No reply.'}
              </span>
              {onRetry && (turn.status === 'failed' || turn.status === 'unavailable') ? (
                <button type="button" className="btn btn-soft" onClick={() => onRetry(turn)}>
                  Retry
                </button>
              ) : null}
            </div>
          ) : (
            <div className={`streaming-text ${streaming ? 'streaming' : ''}`}>
              <Markdown text={turn.text} />
              {streaming && <span className="streaming-caret">|</span>}
            </div>
          )}
          {turn.status === 'cancelled' && !empty ? <div className="turn-notice status-cancelled">
            <svg className="notice-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span className="muted small">Stopped.</span>
          </div> : null}
          {turn.truncated ? <div className="turn-notice status-failed">
            <svg className="notice-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span className="muted small">Reply was cut off at the length limit.</span>
          </div> : null}
          {turn.status === 'failed' && turn.error && !full ? <div className="turn-notice status-failed">
            <svg className="notice-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span className="muted small">Error: {turn.error}</span>
            {onRetry ? (
              <button type="button" className="btn btn-soft" onClick={() => onRetry(turn)}>
                Retry
              </button>
            ) : null}
          </div> : null}
        </div>
      </div>
      {!streaming && !empty ? (
        <div className="turn-actions">
          <button
            type="button"
            className="action-btn"
            aria-label={copied ? 'Copied' : 'Copy'}
            title={copied ? 'Copied' : 'Copy'}
            onClick={() => {
              void navigator.clipboard?.writeText(turn.text).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              });
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              {copied
                ? <path d="M20 6 9 17l-5-5" />
                : <><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" ry="1" /></>}
            </svg>
          </button>
          {turn.status === 'completed' ? (
            <button
              type="button"
              className={`action-btn ${speakingThis ? 'active' : ''}`}
              aria-label={speakingThis ? 'Stop reading aloud' : 'Read this reply aloud'}
              aria-pressed={speakingThis}
              onClick={toggleSpeak}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {speakingThis ? <path d="M11 5 6 9H2v6h4l5 4V5z" /> : <><path d="M11 5 6 9H2v6h4l5 4V5z" /><path d="M15.54 8.46a5 5 0 0 1 0 7.07" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14" /></>}
              </svg>
            </button>
          ) : null}
          {turn.id && turn.status === 'completed' && turn.purpose && turn.purpose !== 'saved' && outputHref ? (
            <Link className="action-btn" to={outputHref(turn)} aria-label="In saved documents" title="In saved documents">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
              </svg>
            </Link>
          ) : turn.id && turn.status === 'completed' && onSave ? (
            <button
              type="button"
              className={`action-btn ${turn.purpose === 'saved' ? 'active' : ''}`}
              aria-label={turn.purpose === 'saved' ? 'Remove from saved documents' : 'Keep in saved documents'}
              title={turn.purpose === 'saved' ? 'Remove from saved documents' : 'Keep in saved documents'}
              onClick={() => void onSave(turn, turn.purpose !== 'saved')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
              </svg>
            </button>
          ) : null}
          {onRate && turn.id && turn.status === 'completed' ? (
            <>
              <button
                type="button"
                className={`action-btn rate-btn ${turn.rating === 'up' ? 'active' : ''}`}
                aria-label="Helpful"
                aria-pressed={turn.rating === 'up'}
                onClick={() => void onRate(turn, turn.rating === 'up' ? null : 'up')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M7 10v12" />
                  <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3-3.88Z" />
                </svg>
              </button>
              <button
                type="button"
                className={`action-btn rate-btn ${turn.rating === 'down' ? 'active' : ''}`}
                aria-label="Not helpful"
                aria-pressed={turn.rating === 'down'}
                onClick={() => void onRate(turn, turn.rating === 'down' ? null : 'down')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M17 14V2" />
                  <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
                </svg>
              </button>
            </>
          ) : null}
          {canPin && onPin && turn.id ? (
            <button
              type="button"
              className="action-btn"
              aria-label="Add to the project notes"
              title="Add to the project notes (decisions, goals, open questions)"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => setPinning({ text: pinTextFor(turn, (e.currentTarget as HTMLElement).closest('.turn')) })}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 17v5" /><path d="M7 10 12 5l5 5" /><path d="M7 10v4a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-4l-5-5-5 5Z" />
              </svg>
            </button>
          ) : null}
        </div>
      ) : null}
      {pinning && onPin ? (
        <PinForm
          initialText={pinning.text}
          busy={pinBusy}
          onCancel={() => setPinning(null)}
          onPin={(pin) => {
            setPinBusy(true);
            onPin(turn, pin)
              .then(() => setPinning(null))
              .catch(() => undefined)
              .finally(() => setPinBusy(false));
          }}
        />
      ) : null}
    </div>
  );
}
